import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { RhSingleAutocomplete } from '../../../../../../../../dum';
import { ID, IOrderSale } from '../../../order-sale-form';
import { useCalculateProductsTotal } from '../../../helpers/use-calculate-products-total';
import { useOrderSaleProductInputQuery } from '../../../../../../../../../services';
import { FIELD_LABELS, shortLabel } from '../../../../../../../../../helpers';
import {
    isProductLockedByReturns,
    PRODUCT_LOCKED_BY_RETURNS_MESSAGE,
} from '../is-product-locked-by-returns';

const ORDER_SALE_PRODUCTS: keyof IOrderSale = 'order_sale_products';
const ORDER_ADJUSTMENT_PRODUCTS: keyof IOrderSale = 'order_adjustment_products';
const ORDER_REQUEST_NAME: keyof IOrderSale = 'order_request';
const PRODUCT_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'product';
const GROUP_WEIGHT_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'group_weight';
const KILO_PRICE_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'kilo_price';
const GROUP_PRICE_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'group_price';

const GROUPS_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'groups';
const KILOS_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'kilos';

interface IProductsInput {
    index: number;
}

export function OrderSaleProductInput({ index }: IProductsInput) {
    const INPUT_NAME = `${ORDER_SALE_PRODUCTS}.${index}.${PRODUCT_FIELD_NAME}`;
    const orderRequest = useWatch({
        name: ORDER_REQUEST_NAME,
    }) as IOrderSale['order_request'];
    const orderSaleProducts = useWatch({
        name: `${ORDER_SALE_PRODUCTS}`,
    }) as IOrderSale['order_sale_products'];

    const orderAdjustmentProducts = useWatch({
        name: ORDER_ADJUSTMENT_PRODUCTS,
    }) as IOrderSale['order_adjustment_products'];

    const id = useWatch({
        name: ID,
    }) as IOrderSale['id'];

    const isLockedByReturns = isProductLockedByReturns(
        orderSaleProducts?.[index],
        orderAdjustmentProducts,
    );

    const { calculateProductsTotal } = useCalculateProductsTotal();
    const { data: products } = useOrderSaleProductInputQuery({
        variables: {
            excludeDiscontinued: !id,
        },
    });

    const formContext = useFormContext();

    const productsData = useMemo(() => {
        return orderRequest
            ? orderRequest.order_request_remaining_products.map(
                  (rp) => rp.product!,
              )
            : products
            ? products?.getProducts
            : [];
    }, [orderRequest, products]);

    return (
        <RhSingleAutocomplete
            itemText={'compound_description'}
            itemValue={'id'}
            returnObject
            options={productsData}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.product_id)}
            disabled={isLockedByReturns}
            helperText={
                isLockedByReturns ? PRODUCT_LOCKED_BY_RETURNS_MESSAGE : undefined
            }
            onChange={(val) => {
                const inputValue =
                    val as IOrderSale['order_sale_products'][number]['product'];

                if (orderRequest?.order_request_remaining_products) {
                    const orderRequestRemainingProduct =
                        orderRequest?.order_request_remaining_products.find(
                            (p) => p?.product?.id === inputValue?.id,
                        );
                    if (orderRequestRemainingProduct && inputValue) {
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUP_WEIGHT_FIELD_NAME}`,
                            orderRequestRemainingProduct.product
                                ?.current_group_weight,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${KILO_PRICE_FIELD_NAME}`,
                            orderRequestRemainingProduct.kilo_price,
                        );
                        // Group-priced products carry their price in group_price
                        // (kilo_price is 0). Without this the request's price never
                        // reached the sale's price input for those products.
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUP_PRICE_FIELD_NAME}`,
                            orderRequestRemainingProduct.group_price,
                        );

                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${KILOS_FIELD_NAME}`,
                            orderRequestRemainingProduct.kilos,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUPS_FIELD_NAME}`,
                            orderRequestRemainingProduct.groups,
                        );
                    } else {
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUP_WEIGHT_FIELD_NAME}`,
                            0,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${KILO_PRICE_FIELD_NAME}`,
                            0,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUP_PRICE_FIELD_NAME}`,
                            0,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${KILOS_FIELD_NAME}`,
                            0,
                        );
                        formContext.setValue(
                            `${ORDER_SALE_PRODUCTS}.${index}.${GROUPS_FIELD_NAME}`,
                            0,
                        );
                    }
                }
                orderSaleProducts.forEach((_, i) => {
                    if (i !== index) {
                        void formContext.trigger(
                            `${ORDER_SALE_PRODUCTS}.${i}.${PRODUCT_FIELD_NAME}`,
                        );
                    }
                });
                calculateProductsTotal();
            }}
            disableGutters
        />
    );
}
